import {useCallback, useEffect, useMemo, useState} from "react";
import {
    Paper, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    IconButton, Typography, Chip, CircularProgress, Box, useTheme, TablePagination,
    TableSortLabel
} from "@mui/material";
import {KeyboardArrowDown, KeyboardArrowRight} from "@mui/icons-material";
import {formatCurrency, formatHierarchyNames, formatNumber, formatPercent} from "../utils/formatters";
import {SEGMENTATION_CALC_COLUMN_LABELS} from "../utils/segmentation";

const SEGMENT_COLORS = {
    defend: {bg: '#2E7D32', fg: '#fff'},
    grow: {bg: '#42A5F5', fg: '#fff'},
    optimize: {bg: '#FB8C00', fg: '#212121'},
    review_and_fix: {bg: '#E53935', fg: '#fff'},
};

const SEGMENT_LABELS = {
    defend: 'Defend',
    grow: 'Grow',
    optimize: 'Optimize',
    review_and_fix: 'Review/Fix',
};

const HIERARCHY_CELL_SX = {
    minWidth: 160,
    maxWidth: 220,
    whiteSpace: 'normal',
    wordBreak: 'break-word'
};

const isExpandableGroupBy = (groupBy) => groupBy === 'account_id';

const getDimensionConfig = (groupBy) => {
    if (groupBy === 'sales_person_id') {
        return {showHierarchy: false, showNumber: false};
    }
    return {showHierarchy: true, showNumber: true};
};

const formatMetricValue = (value, valueType) => {
    if (value === null || value === undefined) {
        return '—';
    }
    if (valueType === 'percent') {
        return formatPercent(value) ?? '0.0%';
    }
    if (valueType === 'count') {
        return formatNumber(value) ?? '0';
    }
    return formatCurrency(value) ?? '$0';
};

const SegmentChip = ({segment}) => {
    if (!segment) return null;
    const colors = SEGMENT_COLORS[segment] || {bg: '#607D8B', fg: '#fff'};
    const label = SEGMENT_LABELS[segment] || segment;
    return <Chip size="small" label={label} style={{background: colors.bg, color: colors.fg, fontWeight: 600}}/>;
};

const getSortValue = (row, sortKey, calcTypes) => {
    if (sortKey === 'name') {
        return row.name || '';
    }
    if (sortKey === 'hierarchy_names') {
        return formatHierarchyNames(row.hierarchy_names) || '';
    }
    if (sortKey === 'number') {
        return row.number ?? '';
    }
    if (sortKey === 'segment') {
        return row.segment || '';
    }
    if (calcTypes.includes(sortKey)) {
        const value = row[sortKey];
        return value?.current ?? value ?? 0;
    }
    return '';
};

const sortRows = (rows, sortKey, sortDir, calcTypes) => {
    if (!sortKey) {
        return rows;
    }
    const direction = sortDir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const aVal = getSortValue(a, sortKey, calcTypes);
        const bVal = getSortValue(b, sortKey, calcTypes);
        if (typeof aVal === 'string' || typeof bVal === 'string') {
            return String(aVal).localeCompare(String(bVal)) * direction;
        }
        if (aVal === bVal) {
            return String(a.name || '').localeCompare(String(b.name || '')) * direction;
        }
        return (aVal - bVal) * direction;
    });
};

const MetricCells = ({row, calcTypes, calcConfigs}) => (
    <>
        {calcTypes.map((calcType) => (
            <TableCell key={calcType} align="right">
                {formatMetricValue(
                    row[calcType]?.current ?? row[calcType],
                    calcConfigs[calcType]?.valueType
                )}
            </TableCell>
        ))}
    </>
);

const ChildRows = ({children, calcTypes, calcConfigs, dimensionConfig, depth}) => (
    children.map((child) => (
        <TableRow key={`${depth}:${child.id}`} style={{background: depth > 1 ? '#FAFAFA' : '#F5F7FB'}}>
            <TableCell style={{paddingLeft: depth === 1 ? 56 : 96}}>
                <Typography variant="body2">{child.name}</Typography>
            </TableCell>
            {dimensionConfig.showHierarchy && (
                <TableCell sx={HIERARCHY_CELL_SX}>
                    <Typography variant="body2" color="textSecondary">
                        {formatHierarchyNames(child.hierarchy_names) || '—'}
                    </Typography>
                </TableCell>
            )}
            {dimensionConfig.showNumber && (
                <TableCell>
                    <Typography variant="body2">{child.number != null ? String(child.number) : '—'}</Typography>
                </TableCell>
            )}
            <TableCell/>
            <MetricCells row={child} calcTypes={calcTypes} calcConfigs={calcConfigs}/>
        </TableRow>
    ))
);

const DataRow = ({
    row,
    groupBy,
    calcTypes,
    calcConfigs,
    dimensionConfig,
    onLoadChildren,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [children, setChildren] = useState(null);
    const [loading, setLoading] = useState(false);
    const isExpandable = isExpandableGroupBy(groupBy);

    const toggle = () => {
        if (!isExpandable) {
            return;
        }
        const next = !expanded;
        setExpanded(next);
        if (next && children === null) {
            setLoading(true);
            onLoadChildren(row, groupBy, (result) => {
                setChildren(result?.breakdown || []);
                setLoading(false);
            });
        }
    };

    return (
        <>
            <TableRow hover={isExpandable} onClick={toggle} style={{cursor: isExpandable ? 'pointer' : 'default'}}>
                <TableCell>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {isExpandable ? (
                            <IconButton size="small">
                                {loading
                                    ? <CircularProgress size={14}/>
                                    : expanded
                                        ? <KeyboardArrowDown fontSize="small"/>
                                        : <KeyboardArrowRight fontSize="small"/>}
                            </IconButton>
                        ) : (
                            <Box sx={{width: 34, flexShrink: 0}}/>
                        )}
                        <Typography variant="body2" style={{fontWeight: 600}}>{row.name}</Typography>
                    </Box>
                </TableCell>
                {dimensionConfig.showHierarchy && (
                    <TableCell sx={HIERARCHY_CELL_SX}>
                        <Typography variant="body2" color="textSecondary">
                            {formatHierarchyNames(row.hierarchy_names) || '—'}
                        </Typography>
                    </TableCell>
                )}
                {dimensionConfig.showNumber && (
                    <TableCell>
                        <Typography variant="body2">{row.number != null ? String(row.number) : '—'}</Typography>
                    </TableCell>
                )}
                <TableCell><SegmentChip segment={row.segment}/></TableCell>
                <MetricCells row={row} calcTypes={calcTypes} calcConfigs={calcConfigs}/>
            </TableRow>
            {expanded && !loading && children && (
                <ChildRows
                    children={children}
                    calcTypes={calcTypes}
                    calcConfigs={calcConfigs}
                    dimensionConfig={getDimensionConfig('product_id')}
                    depth={1}
                />
            )}
        </>
    );
};

export const SegmentationDetailTable = ({
    breakdown = [],
    calcConfigs = {},
    groupBy = 'account_id',
    loading = false,
    onLoadChildren,
}) => {
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    const calcTypes = useMemo(() => Object.keys(calcConfigs), [calcConfigs]);
    const dimensionConfig = useMemo(() => getDimensionConfig(groupBy), [groupBy]);

    useEffect(() => {
        setPage(0);
    }, [breakdown, groupBy]);

    const sortedRows = useMemo(
        () => sortRows(breakdown, sortKey, sortDir, calcTypes),
        [breakdown, sortKey, sortDir, calcTypes]
    );

    const paginatedRows = useMemo(
        () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedRows, page, rowsPerPage]
    );

    const columnCount = 1
        + (dimensionConfig.showHierarchy ? 1 : 0)
        + (dimensionConfig.showNumber ? 1 : 0)
        + 1
        + calcTypes.length;

    const handleSort = (field) => {
        if (sortKey === field) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(field);
            setSortDir('asc');
        }
        setPage(0);
    };

    const renderSortLabel = (label, field) => (
        <TableSortLabel
            active={sortKey === field}
            direction={sortKey === field ? sortDir : 'asc'}
            onClick={() => handleSort(field)}
        >
            {label}
        </TableSortLabel>
    );

    return (
        <Paper style={{...theme.palette.contentPanel, padding: 0}}>
            <TableContainer>
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        '& .MuiTableCell-head.MuiTableCell-stickyHeader': {
                            zIndex: 1,
                            backgroundColor: '#F5F7FB'
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell style={{fontWeight: 600}}>{renderSortLabel('Name', 'name')}</TableCell>
                            {dimensionConfig.showHierarchy && (
                                <TableCell style={{fontWeight: 600}}>{renderSortLabel('Hierarchy', 'hierarchy_names')}</TableCell>
                            )}
                            {dimensionConfig.showNumber && (
                                <TableCell style={{fontWeight: 600}}>{renderSortLabel('Number', 'number')}</TableCell>
                            )}
                            <TableCell style={{fontWeight: 600}}>{renderSortLabel('Segment', 'segment')}</TableCell>
                            {calcTypes.map((calcType) => (
                                <TableCell key={calcType} align="right" style={{fontWeight: 600}}>
                                    {renderSortLabel(
                                        SEGMENTATION_CALC_COLUMN_LABELS[calcType] || calcConfigs[calcType]?.name || calcType,
                                        calcType
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={columnCount} style={{textAlign: 'center', padding: 30}}>
                                    <CircularProgress size={24}/>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && breakdown.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columnCount} style={{textAlign: 'center', padding: 30}}>
                                    <Typography color="textSecondary">No data available</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && paginatedRows.map((row) => (
                            <DataRow
                                key={row.id}
                                row={row}
                                groupBy={groupBy}
                                calcTypes={calcTypes}
                                calcConfigs={calcConfigs}
                                dimensionConfig={dimensionConfig}
                                onLoadChildren={onLoadChildren}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {!loading && breakdown.length > 0 && (
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    count={breakdown.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}
        </Paper>
    );
};
